import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useActiveVehicle } from '../../../context/ActiveVehicleProvider';
import { useNotificationProvider } from 'hooks';
import { normalizePhotoOrientation } from '../../../utils/normalizePhotoOrientation';

const ActiveVehicleDrawerCarPhotoStage: React.FC = () => {
	const {
		uppyTicket,
		openModal,
		setOpenModal,
		goNext,
		ticketFormMethods,
		parkingLocation,
	} = useActiveVehicle();
	const { notifyError } = useNotificationProvider();

	const fileInputRef = React.useRef<HTMLInputElement | null>(null);
	// Snapshot the attachment count when the operator enters this stage so we
	// can detect whether *this* stage produced one — protects against the
	// pre-existing license-plate scan that's already attached.
	const initialCountRef = React.useRef<number>(
		ticketFormMethods.getValues('attachments')?.length ?? 0,
	);

	const attachments = ticketFormMethods.watch('attachments');
	const attachmentsCount = attachments?.length ?? 0;

	// Advance to the ticket stage once Uppy has finished pushing a new file
	// into the form (works for both Take Photo and Attach Image — both routes
	// open the Uppy modal and only land in `attachments` after upload).
	React.useEffect(() => {
		if (openModal) return;
		if (attachmentsCount > initialCountRef.current) {
			goNext();
		}
	}, [openModal, attachmentsCount, goNext]);

	const handlePhotoChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file || !uppyTicket) return;

		try {
			const normalized = await normalizePhotoOrientation(file);
			const uniqueName = `${Date.now()}_${normalized.name || 'photo.jpg'}`;
			await uppyTicket.addFile({
				name: uniqueName,
				type: normalized.type || 'image/jpeg',
				data: normalized,
				source: 'camera',
				meta: { relativePath: uniqueName },
			});
			setOpenModal(true);
		} catch (err) {
			console.error('[CarPhotoStage] Failed to add photo:', err);
			notifyError('Failed to add photo. Please try again.');
		}
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const takePhoto = () => {
		fileInputRef.current?.click();
	};

	const handleAttach = () => {
		setOpenModal(true);
	};

	const handleSkip = () => {
		goNext();
	};

	return (
		<Box
			sx={{
				flex: 1,
				width: '100%',
				maxWidth: 600,
				mx: 'auto',
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			}}
		>
			<Box sx={{ px: 2, pt: 1, pb: 2, flexShrink: 0 }}>
				<Typography variant="h6" fontWeight={500} gutterBottom>
					Take a vehicle photo
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Capture the entire vehicle so the record has a clear
					photo of it.
				</Typography>
				{parkingLocation && (
					<Typography
						variant="caption"
						color="text.secondary"
						component="div"
						sx={{ mt: 1 }}
					>
						{parkingLocation.name}
					</Typography>
				)}
			</Box>

			<Box sx={{ flexGrow: 1, minHeight: 0 }} />

			<Box
				data-tour="ticket-car-photo"
				sx={{
					px: 2,
					pt: 1,
					pb: 'calc(16px + env(safe-area-inset-bottom))',
					display: 'flex',
					flexDirection: 'column',
					gap: 1.5,
					flexShrink: 0,
				}}
			>
				<Button
					variant="contained"
					color="primary"
					size="large"
					fullWidth
					startIcon={<CameraAltIcon />}
					onClick={takePhoto}
					sx={{
						py: 2,
						borderRadius: 3,
						fontSize: '1rem',
						fontWeight: 500,
						textTransform: 'none',
						boxShadow: 3,
					}}
				>
					Take Photo
				</Button>
				<Button
					variant="outlined"
					color="secondary"
					size="large"
					fullWidth
					startIcon={<AttachFileIcon />}
					onClick={handleAttach}
					sx={{
						py: 1.5,
						borderRadius: 2,
						fontSize: '1rem',
						fontWeight: 500,
						textTransform: 'none',
					}}
				>
					Attach Image
				</Button>
				<Box display="flex" justifyContent="center">
					<Button
						variant="text"
						size="small"
						onClick={handleSkip}
						sx={{
							color: 'text.secondary',
							textTransform: 'none',
							fontWeight: 400,
						}}
					>
						Skip
					</Button>
				</Box>
			</Box>

			<input
				type="file"
				accept="image/*"
				capture="environment"
				style={{ display: 'none' }}
				ref={fileInputRef}
				onChange={handlePhotoChange}
			/>
		</Box>
	);
};

export default ActiveVehicleDrawerCarPhotoStage;
